/* eslint-disable @typescript-eslint/no-explicit-any */
import './converge-global-search-field.scss';
import { Box, Pagination, Skeleton, Stack } from '@mui/material';
import {
  ISearchResultGroup,
  ISearchResultItem,
  SearchGroup,
  SearchV2FilterInput,
  useSearch,
} from '@converge/graphql';
import { Link, LinkProps, useLocation } from 'react-router-dom';
import React, {
  Dispatch,
  ForwardedRef,
  ReactNode,
  SetStateAction,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { convergeLogger } from '@converge/config';
import { createDurationBetween, formatDuration } from '@converge/utils';

import { ApolloError } from '@apollo/client';
import CircularProgress from '@mui/material/CircularProgress';
import { ConvergeIcon } from './converge-icon';
import { ConvergeTooltip } from './converge-tooltip';
import { Duration } from 'moment';
import { Pluralized } from './pluralized';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import { captureException } from '@sentry/react';
import debounce from 'lodash.debounce';
import pluralize from 'pluralize';
import { useConvergeSnackBar } from '../../contexts/use-snackbar';
import { highlightText } from '../utils/highlight-text';

const PAGE_SIZE = 20;

export interface IConvergeGlobalSearchFieldProps {
  initialQuery: string;
  onChangeQuery: (query: string) => void;
  searchResultGroups?: ISearchResultGroup[];
  searchResults?: ISearchResultItem[];
  loading?: boolean;
  error?: ApolloError;
  previousTotalResults?: number;
  totalResults?: number;
  hasData: boolean;
  queryText?: string | null;
  remainingResults?: number;
  pageNumber?: number;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
  search: (
    searchText: string,
    after?: number,
    pageSize?: number
  ) => Promise<void>;
  called: boolean;
  isBrokerSearch?: boolean;
}

export interface IConvergeGlobalSearchField {
  setIsVisible: Dispatch<SetStateAction<boolean>>;
}

export const ConvergeGlobalSearchField = (
  props: IConvergeGlobalSearchField
) => {
  const [query, setQuery] = useState<string>('');

  return (
    <ConvergeGlobalSearchV2Field
      setIsVisible={props.setIsVisible}
      initialQuery={query}
      onChangeQuery={(query) => {
        setQuery(query);
      }}
    />
  );
};

interface IConvergeGlobalSearchVersionFieldProps {
  initialQuery: string;
  onChangeQuery: (query: string) => void;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
}

export const ConvergeGlobalSearchV2Field = ({
  initialQuery,
  onChangeQuery,
  setIsVisible,
}: IConvergeGlobalSearchVersionFieldProps) => {
  const { pathname } = useLocation();

  const isSubmissionSearch = pathname.startsWith('/underwriting');
  const isInsuredSearch = pathname.startsWith('/insureds');
  const isBrokerageSearch = pathname.startsWith('/producers/brokerage');
  const isBrokerSearch = pathname.startsWith('/producers/broker');
  const isClaimSearch = pathname.startsWith('/claims');
  const {
    search,
    result,
    searchResultsGrouped,
    remainingResults,
    searchResults,
  } = useSearch();

  const {
    search: searchInsured,
    result: insuredResult,
    searchResultsGrouped: insuredSearchResultsGrouped,
    remainingResults: insuredRemainingResults,
    searchResults: insuredSearchResults,
  } = useSearch();

  const {
    search: searchClaim,
    result: claimResult,
    searchResultsGrouped: claimSearchResultsGrouped,
    remainingResults: claimRemainingResults,
    searchResults: claimSearchResults,
  } = useSearch();

  const {
    search: searchBrokerage,
    result: brokerageResult,
    searchResultsGrouped: brokerageSearchResultsGrouped,
    remainingResults: brokerageRmainingResults,
    searchResults: brokerageSearchResults,
  } = useSearch();

  const {
    search: searchBroker,
    result: brokerResult,
    searchResultsGrouped: brokerSearchResultsGrouped,
    remainingResults: brokerRmainingResults,
    searchResults: brokerSearchResults,
  } = useSearch();

  const { notify } = useConvergeSnackBar();

  const currentResults: any = useMemo(() => {
    if (isSubmissionSearch) {
      return {
        result,
        searchResultsGrouped,
        remainingResults,
        searchResults,
      };
    } else if (isInsuredSearch) {
      return {
        result: insuredResult,
        searchResultsGrouped: insuredSearchResultsGrouped,
        remainingResults: insuredRemainingResults,
        searchResults: insuredSearchResults,
      };
    } else if (isBrokerageSearch) {
      return {
        result: brokerageResult,
        searchResultsGrouped: brokerageSearchResultsGrouped,
        remainingResults: brokerageRmainingResults,
        searchResults: brokerageSearchResults,
      };
    } else if (isBrokerSearch) {
      return {
        result: brokerResult,
        searchResultsGrouped: brokerSearchResultsGrouped,
        remainingResults: brokerRmainingResults,
        searchResults: brokerSearchResults,
      };
    } else if (isClaimSearch) {
      return {
        result: claimResult,
        searchResultsGrouped: claimSearchResultsGrouped,
        remainingResults: claimRemainingResults,
        searchResults: claimSearchResults,
      };
    } else {
      return;
    }
  }, [
    isSubmissionSearch,
    isInsuredSearch,
    isBrokerageSearch,
    isBrokerSearch,
    isClaimSearch,
    result,
    searchResultsGrouped,
    remainingResults,
    searchResults,
    insuredResult,
    insuredSearchResultsGrouped,
    insuredRemainingResults,
    insuredSearchResults,
    brokerageResult,
    brokerageSearchResultsGrouped,
    brokerageRmainingResults,
    brokerageSearchResults,
    brokerResult,
    brokerSearchResultsGrouped,
    brokerRmainingResults,
    brokerSearchResults,
    claimResult,
    claimSearchResultsGrouped,
    claimRemainingResults,
    claimSearchResults,
  ]);

  const handleError = (cause: ApolloError, searchText: string) => {
    convergeLogger.error(`Failed to search for "${searchText}"`, cause);
    const errorText = `${cause instanceof Error ? cause.message : cause}`;
    notify({
      message: `Search failed: ${
        errorText.startsWith('Failed to invoke procedure')
          ? 'Internal server error'
          : errorText.length > 200
          ? errorText.substring(0, 60) + '...'
          : errorText
      }`,
      variant: 'error',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  };

  const previousTotalResults = useMemo(
    () =>
      currentResults?.result?.previousData?.searchV3?.pageInfo?.totalResults,
    [currentResults]
  );

  const totalResults = useMemo(
    () => currentResults?.result?.data?.searchV3?.pageInfo?.totalResults,
    [currentResults]
  );

  const pageNumber = useMemo(
    () => currentResults?.result?.data?.searchV3?.pageInfo?.pageNumber,
    [currentResults]
  );
  return (
    <ConvergeGlobalSearchFieldInner
      setIsVisible={setIsVisible}
      initialQuery={initialQuery}
      onChangeQuery={onChangeQuery}
      searchResults={currentResults?.searchResults}
      called={currentResults?.result?.called}
      remainingResults={currentResults?.remainingResults}
      hasData={!!currentResults?.result?.data}
      error={currentResults?.result?.error}
      loading={currentResults?.result?.loading}
      searchResultGroups={currentResults?.searchResultsGrouped}
      previousTotalResults={previousTotalResults}
      totalResults={totalResults}
      queryText={currentResults?.result?.variables?.filters?.query}
      pageNumber={pageNumber}
      isBrokerSearch={isBrokerSearch}
      search={async (searchText, after, pageSize) => {
        const filters: SearchV2FilterInput = {
          query: searchText,
          paginateAfter: true,
        };

        if (isSubmissionSearch) {
          await search({
            variables: {
              sort: {
                key: 'score',
                order: 'DESC',
              },
              filters: {
                ...filters,
                context: 'submissions',
              },
              pagination: {
                first: pageSize,
                after,
              },
            },
            fetchPolicy: 'network-only',
            onError: (cause) => handleError(cause, searchText),
          });
        } else if (isInsuredSearch) {
          await searchInsured({
            variables: {
              sort: {
                key: 'score',
                order: 'DESC',
              },
              filters: {
                ...filters,
                context: 'insureds',
              },
              pagination: {
                first: pageSize,
                after,
              },
            },
            fetchPolicy: 'network-only',
            onError: (cause) => handleError(cause, searchText),
          });
        } else if (isBrokerageSearch) {
          await searchBrokerage({
            variables: {
              sort: {
                key: 'score',
                order: 'DESC',
              },
              filters: {
                ...filters,
                context: 'brokerages',
              },
              pagination: {
                first: pageSize,
                after,
              },
            },
            fetchPolicy: 'network-only',
            onError: (cause) => handleError(cause, searchText),
          });
        } else if (isBrokerSearch) {
          await searchBroker({
            variables: {
              sort: {
                key: 'score',
                order: 'DESC',
              },
              filters: {
                ...filters,
                context: 'brokers',
              },
              pagination: {
                first: pageSize,
                after,
              },
            },
            fetchPolicy: 'network-only',
            onError: (cause) => handleError(cause, searchText),
          });
        } else {
          await searchClaim({
            variables: {
              filters: {
                ...filters,
                context: 'claims',
              },
              sort: {
                key: 'score',
                order: 'DESC',
              },
              pagination: {
                first: pageSize,
                after,
              },
            },
            fetchPolicy: 'network-only',
            onError: (cause) => handleError(cause, searchText),
          });
        }
      }}
    />
  );
};

export const ConvergeGlobalSearchFieldInner = ({
  searchResultGroups,
  loading,
  error,
  previousTotalResults,
  search,
  hasData,
  totalResults,
  queryText,
  pageNumber,
  remainingResults,
  searchResults,
  onChangeQuery,
  initialQuery,
  called,
  setIsVisible,
  isBrokerSearch,
}: IConvergeGlobalSearchFieldProps) => {
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const [pageSize] = useState<number>(PAGE_SIZE);
  const [showSearchPane, setShowSearchPane] = useState<boolean>(false);
  const [searchText, setSearchText] = useState(initialQuery);
  const [durations, setDurations] = useState<Duration[]>([]);
  const lastDuration = durations[durations.length - 1];
  const searchTextTrimmed = useMemo(() => searchText.trim(), [searchText]);
  const containerRef = useRef<HTMLDivElement>(null);

  // On click outside or ESC key is pressed, close the search results:
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowSearchPane(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowSearchPane(false);
      }
    };

    document.addEventListener('keyup', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keyup', handleEscape);
    };
  }, []);

  const durationText = useMemo(() => {
    if (!lastDuration) {
      return '';
    }
    return formatDuration(lastDuration);
  }, [lastDuration]);

  const { runSearch, runSearchDebounced } = useMemo(() => {
    const runSearch =
      (mode: 'debounced' | 'immeadiate') =>
      async (searchText: string, after = 0) => {
        if (!searchText) {
          setShowSearchPane(false);
          return;
        }

        if (mode === 'debounced' && searchText.trim().length < 3) {
          convergeLogger.debug(
            `Search text is less than 3 characters, so not searching.`
          );
          return;
        }

        const start = new Date();

        convergeLogger.info(
          `Searching for "${searchText}" (after: ${after}) in ${mode} mode...`
        );

        await search(searchText, after, pageSize);

        const end = new Date();

        setDurations((durations) => [
          ...durations,
          createDurationBetween(end, start),
        ]);

        setShowSearchPane(true);

        searchResultsRef.current?.scrollTo({
          top: 0,
          behavior: 'smooth',
          left: 0,
        });
      };
    return {
      runSearch: runSearch('immeadiate'),
      runSearchDebounced: debounce(runSearch('debounced'), 500),
    };
  }, [pageSize, search]);

  useEffect(() => {
    if (initialQuery && !called) {
      runSearchDebounced(initialQuery);
    }
  }, [called, error, initialQuery, runSearch, runSearchDebounced]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChangeQuery(event.target.value);
      setSearchText(event.target.value);
      runSearchDebounced(event.target.value.trim());
    },
    [onChangeQuery, runSearchDebounced]
  );

  const problems = useMemo(() => {
    const { problems, expectedNumberOfSearchResults, error } =
      getProblemsWithSearchResults({
        pageSize,
        totalResults,
        pageNumber,
        searchResults,
        query: searchTextTrimmed,
        isBrokerSearch,
      });

    if (error) {
      convergeLogger.error(error);

      captureException(error, {
        contexts: {
          search: {
            query: searchTextTrimmed,
            searchResults: searchResults ?? [],
            totalResults,
            pageNumber,
            pageSize,
            problems,
            expectedNumberOfSearchResults,
          },
        },
      });
    }

    return { problems, error };
  }, [
    pageNumber,
    pageSize,
    searchResults,
    searchTextTrimmed,
    totalResults,
    isBrokerSearch,
  ]);

  const locationNow = useLocation().pathname.split('/');
  const placeholderMessage = () => {
    let showMessage = '';
    switch (locationNow[1]) {
      case 'underwriting':
        showMessage = 'Submissions';
        break;
      case 'insureds':
        showMessage = 'Insureds';
        break;
      case 'claims':
        showMessage = 'Claims';
        break;
      case 'producers':
        showMessage = 'Producer';
        break;
      default:
        showMessage = ''; // Fallback case
    }
    return `Search ${showMessage}`;
  };

  return (
    <div
      className="search-container"
      data-show-search-pane={`${showSearchPane}`}
      ref={containerRef}
    >
      <TextField
        style={{ backgroundColor: 'white' }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: 'none', // Default border color
            },
            '&:hover fieldset': {
              border: 'none', // Border color on hover
            },
            '&.Mui-focused fieldset': {
              border: 'none', // Border color when focused
            },
          },
        }}
        variant="outlined"
        placeholder={placeholderMessage()}
        size="small"
        fullWidth
        type="search"
        className="search-field"
        value={searchText}
        onChange={handleSearchChange}
        onFocus={() => {
          if (!showSearchPane && (searchTextTrimmed || hasData)) {
            setShowSearchPane(true);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && searchTextTrimmed.length >= 3) {
            runSearch(searchTextTrimmed);
          }
        }}
        InputProps={{
          slotProps: {
            input: {
              maxLength: 1000,
            },
          },
          endAdornment: (
            <EndAdornment
              loading={loading}
              runSearch={runSearch}
              searchTextTrimmed={searchTextTrimmed}
              setIsVisible={setIsVisible}
            />
          ),
        }}
      />

      {(showSearchPane || (!hasData && loading)) && (
        <div className="search-pane">
          <div className="search-pane-inner">
            <div className="search-results" ref={searchResultsRef}>
              <SearchResultsHeader
                hasData={hasData}
                totalResults={totalResults}
                durationText={durationText}
                loading={loading}
                error={error}
                queryText={queryText}
                problems={problems.problems}
              />

              {!hasData && loading && (
                <>
                  <Skeleton className="skeleton-1" />
                  <Skeleton className="skeleton-2" />
                  <Skeleton className="skeleton-3" />
                </>
              )}

              {(searchResultGroups ?? []).map((group, index) => {
                return (
                  <SearchResultGroup
                    key={index}
                    setShowSearchPane={setShowSearchPane}
                    group={group}
                    queryText={queryText}
                  />
                );
              })}
            </div>

            <SearchPagination
              hasData={hasData}
              totalResults={totalResults}
              previousTotalResults={previousTotalResults}
              pageSize={pageSize}
              pageNumber={pageNumber}
              remainingResults={remainingResults}
              query={searchTextTrimmed}
              loading={loading}
              onPageChange={({ page, after }) => {
                convergeLogger.info(
                  `Page changed to ${page} (after: ${after})`
                );
                runSearch(searchTextTrimmed, after);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface ISearchPaginationProps {
  hasData: boolean;
  totalResults: number | undefined;
  previousTotalResults: number | undefined;
  pageSize: number;
  pageNumber: number | undefined;
  remainingResults: number | undefined;
  query: string;
  loading: boolean | undefined;
  onPageChange: ({ page, after }: { page: number; after: number }) => void;
}

const SearchFooter = ({ pagination }: { pagination: ReactNode }) => {
  return (
    <div className="search-footer">
      <div className="search-pagination">{pagination}</div>
    </div>
  );
};

const SearchPagination = ({
  hasData,
  totalResults,
  previousTotalResults,
  pageSize,
  pageNumber,
  remainingResults,
  loading,
  onPageChange,
}: ISearchPaginationProps) => {
  if (
    !hasData &&
    loading &&
    previousTotalResults &&
    pageSize < previousTotalResults
  ) {
    return (
      <SearchFooter
        pagination={
          <Pagination
            page={pageNumber}
            showFirstButton
            showLastButton
            size={'small'}
            variant="text"
            shape="rounded"
            count={Math.ceil(previousTotalResults / pageSize)}
            disabled
            className="skeleton-animation"
          />
        }
      />
    );
  }

  if (remainingResults && remainingResults > 0 && totalResults && hasData) {
    return (
      <SearchFooter
        pagination={
          <Pagination
            page={pageNumber}
            showFirstButton
            showLastButton
            size={'small'}
            variant="text"
            shape="rounded"
            count={Math.ceil(totalResults / pageSize)}
            onChange={(_, page) => {
              const after = pageSize * (page - 1);
              onPageChange({
                page,
                after,
              });
            }}
          />
        }
      />
    );
  }

  return null;
};

export const getUrlBaseFromSearchGroup = (
  group: SearchGroup | null | undefined
): string => {
  switch (group) {
    case SearchGroup.Policy:
      return '/policies';
    case SearchGroup.Alias:
      return '/aliases';
    case SearchGroup.Broker:
      return '/producers/brokerage';
    case SearchGroup.Endorsement:
      return '/endorsements';
    case SearchGroup.Insured:
      return '/insureds';
    case SearchGroup.Quote:
      return '/quotes';
    case SearchGroup.Subjectivity:
      return '/subjectivities';
    case SearchGroup.Submission:
      return '/underwriting';
    case SearchGroup.Entity:
      return '/entities';
    case SearchGroup.Claim:
      return '/claims';
    default:
      return `/${pluralize(group ?? 'misc').toLowerCase()}`;
  }
};

export const searchResultGroupCanBeLinked = (
  group: SearchGroup | null | undefined
) => {
  if (!group) {
    return false;
  }

  return ![
    SearchGroup.Subjectivity,
    SearchGroup.Alias,
    SearchGroup.Endorsement,
    'OTHER',
  ].includes(group);
};

export const getUrlFromSearchResult = (
  result: ISearchResultItem,
  isBrokerageSearch?: boolean
): string => {
  if (result.group === 'ENTITY' && !isBrokerageSearch) {
    return `insureds/${result.uuid}`;
  }

  if (result.group === 'ENTITY' && isBrokerageSearch) {
    return `producers/brokerage/${result.uuid}`;
  }

  if (result.group === 'BROKER') {
    return `producers/brokerage/${result.uuid}`;
  }

  if (result.group === 'PERSON') {
    return `producers/broker/${result.uuid}`;
  }
  return `${getUrlBaseFromSearchGroup(result.group)}/${result.uuid}`;
};

interface ISearchResultLinkProps extends Omit<LinkProps, 'to'> {
  result: ISearchResultItem;
  children: ReactNode;
}

export const SearchResultLink = forwardRef<
  HTMLAnchorElement,
  ISearchResultLinkProps
>(({ result, children, ...props }, ref) => {
  const { pathname } = useLocation();

  const isBrokerageSearch = pathname.startsWith('/producers/brokerage');

  const url = useMemo(
    () => getUrlFromSearchResult(result, isBrokerageSearch),
    [result, isBrokerageSearch]
  );

  return (
    <Link to={url} ref={ref as ForwardedRef<HTMLAnchorElement>} {...props}>
      {children}
    </Link>
  );
});

const getProblemsWithSearchResults = ({
  pageNumber,
  pageSize,
  searchResults,
  totalResults,
  query,
  isBrokerSearch,
}: {
  pageSize: number;
  totalResults: number | undefined;
  pageNumber: number | undefined;
  searchResults: ISearchResultItem[] | undefined;
  query: string;
  isBrokerSearch?: boolean;
}): {
  problems: string[];
  expectedNumberOfSearchResults: number;
  error?: Error;
} => {
  const expectedNumberOfSearchResults = Math.min(
    pageSize,
    Math.max(
      0,
      totalResults ? totalResults - pageSize * ((pageNumber ?? 1) - 1) : 0
    )
  );

  const problems: string[] = [];

  if (searchResults) {
    if (searchResults.length > pageSize) {
      problems.push(
        `The number of search results (${pluralize(
          'result',
          searchResults.length,
          true
        )}) returned for this search results page exceeds the maximum page size of ${pageSize}.`
      );
    }

    if (totalResults && searchResults.length > totalResults) {
      problems.push(
        `The number of search results (${pluralize(
          'result',
          searchResults.length,
          true
        )}) on this page exceeds the total reported results of ${totalResults}.`
      );
    }

    const searchResultsWithoutAName = (
      searchResults as ISearchResultItem[]
    ).filter(
      (result) =>
        !`${getSearchResultName(result) || ''}`?.trim() && !isBrokerSearch
    );

    if (searchResultsWithoutAName.length > 0) {
      problems.push(
        `${pluralize(
          'search result',
          searchResultsWithoutAName.length,
          true
        )} ${
          searchResultsWithoutAName.length === 1 ? 'is' : 'are'
        } missing a name.`
      );
    }
  }
  return {
    problems,
    expectedNumberOfSearchResults,
    error:
      problems.length > 0
        ? new Error(
            `Searching for "${
              query || ''
            }" resulted in the following problems: ${problems
              .map((problem, index) => `${index + 1}) ${problem}`)
              .join(', ')}`
          )
        : undefined,
  };
};

const EndAdornment = ({
  loading,
  setIsVisible,
}: {
  loading: boolean | undefined;
  runSearch: (searchText: string, after?: number) => Promise<void>;
  searchTextTrimmed: string;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
}) => {
  return (
    <Stack
      marginLeft={1}
      justifyContent={'center'}
      alignItems={'center'}
      gap={1}
      flexDirection={'row'}
    >
      {loading ? (
        <CircularProgress
          color="inherit"
          size={20}
          sx={{ marginLeft: '4px' }}
        />
      ) : (
        <SearchIcon
          onClick={() => setIsVisible(false)}
          sx={{
            cursor: 'pointer',
            zIndex: '20',
            paddingBottom: '0px',
            '&:hover': {
              opacity: 0.7,
            },
          }}
        />
      )}
      <Stack
        style={{
          width: '35px',
          height: '35px',
          backgroundColor: '#08F0D1',
          zIndex: '9',
          position: 'absolute',
          borderTopRightRadius: '4px',
          borderBottomRightRadius: '4px',
          top: 0,
        }}
      ></Stack>
    </Stack>
  );
};

const NoSearchResultsFound = ({
  queryText,
}: {
  queryText: string | null | undefined;
}) => {
  return (
    <div className="search-no-results">
      <div className="search-no-results-content">
        <p>No results {queryText && <>for "{queryText}"</>}</p>

        <Stack className="suggestionMessage">
          <Box> Make sure the words are spelled correctly</Box>
          <Box>or try different keywords.</Box>
        </Stack>
      </div>
    </div>
  );
};

const SearchResultsHeader = ({
  hasData,
  totalResults,
  durationText,
  loading,
  error,
  queryText,
  problems,
}: {
  hasData: boolean;
  totalResults: number | undefined;
  durationText: string;
  loading: boolean | undefined;
  error: ApolloError | undefined;
  queryText: string | null | undefined;
  problems: string[];
}) => {
  return (
    <div className="search-section">
      <div className="search-section-header">
        <p className="search-results-stats">
          {hasData ? (
            <span>
              {totalResults}{' '}
              <Pluralized count={totalResults}>result</Pluralized>{' '}
              {durationText && <>({durationText})</>}
            </span>
          ) : loading ? (
            <span>Searching ...</span>
          ) : error ? (
            <span>
              <strong>Search failed</strong>: {error.message}
            </span>
          ) : (
            <span>No search results</span>
          )}
        </p>
        {totalResults === 0 && <NoSearchResultsFound queryText={queryText} />}

        {problems.length > 0 && (
          <p className="search-results-stats">
            <ConvergeIcon type="warning" />{' '}
            <span>
              <strong>Warning:</strong>{' '}
              {problems
                .map((problem, index) =>
                  problems.length > 1 ? `${index + 1}) ${problem}` : problem
                )
                .join(' ')}{' '}
              {problems.length === 1
                ? 'This problem has'
                : 'These problems have'}{' '}
              been reported to the developers.
            </span>
          </p>
        )}
      </div>
    </div>
  );
};

const STATUS_COLORS: Record<string, string> = {
  // Submission — green
  BOUND: '#2E7D32',
  CLEARED: '#2E7D32',
  EVALUATION: '#2E7D32',
  QUOTED: '#2E7D32',
  SUBMITTED: '#2E7D32',
  // Submission — orange
  HOLD: '#EF6C00',
  INDICATED: '#EF6C00',
  REFERRED: '#EF6C00',
  REQUESTED_MORE_INFO: '#EF6C00',
  // Submission — blue
  POLICY_ISSUED: '#0288D1',
  // Submission — red
  CLOSED: '#D32F2F',
  DECLINED: '#D32F2F',
  LOST: '#D32F2F',
  // Claim
  OPEN: '#2E7D32',
  REOPENED: '#0288D1',
  // Entity / Person
  ACTIVE: '#2E7D32',
  INACTIVE: '#D32F2F',
};

const getStatusColor = (status: string) =>
  STATUS_COLORS[status.toUpperCase()] ?? '#757575';

const formatStatusLabel = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const SearchResultItem = ({
  result,
  queryText,
  setShowSearchPane,
  group,
}: {
  result: ISearchResultItem;
  queryText?: string | null;
  setShowSearchPane: (show: boolean) => void;
  group: ISearchResultGroup;
}) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const [tooltipEnabled, setTooltipEnabled] = useState(false);

  const handleMouseEnter = () => {
    if (nameRef.current) {
      setTooltipEnabled(
        nameRef.current.scrollWidth > nameRef.current.clientWidth
      );
    }
  };

  const tooltipText = getSearchResultName(result) ?? '';
  const showLink = searchResultGroupCanBeLinked(result.group);

  const Inner = () => (
    <div className="search-section-result-inner">
      <div className="search-section-result-inner-row">
        <div
          className="search-primary-info"
          ref={nameRef}
          onMouseEnter={handleMouseEnter}
        >
          <span>
            {highlightText(getSearchResultName(result) ?? '', queryText)}
          </span>
        </div>
      </div>
      <div className="search-primary-extra-info">
        {getSearchResultDescription(result)}
        {result.status && (
          <span
            className="search-status-label"
            style={{ color: getStatusColor(result.status) }}
          >
            {' '}
            ({formatStatusLabel(result.status)})
          </span>
        )}
      </div>
    </div>
  );

  return (
    <ConvergeTooltip
      title={tooltipEnabled ? tooltipText : ''}
      disabled={!tooltipEnabled}
    >
      {showLink ? (
        <SearchResultLink
          result={result}
          className="search-result-link"
          onClick={() => setShowSearchPane(false)}
        >
          <Inner />
        </SearchResultLink>
      ) : (
        <div className="search-result-link">
          <Inner />
        </div>
      )}
    </ConvergeTooltip>
  );
};

const SearchResultGroup = ({
  group,
  setShowSearchPane,
  queryText,
}: {
  group: ISearchResultGroup;
  setShowSearchPane: (show: boolean) => void;
  queryText?: string | null;
}) => {
  return (
    <div className="search-section">
      <div className="search-section-header">
        <div className="search-section-header-headline-container"></div>
      </div>
      {group?.results?.map((result, index) => (
        <div className="search-section-result" key={index}>
          <SearchResultItem
            result={result}
            queryText={queryText}
            setShowSearchPane={setShowSearchPane}
            group={group}
          />
        </div>
      ))}
    </div>
  );
};

export const getSearchResultName = (result: ISearchResultItem) => {
  return result.name ?? result.referenceId;
};

export const getSearchResultDescription = (result: ISearchResultItem) => {
  return result.description
    ? result.description
    : result.name
    ? result.referenceId
    : '';
};
